import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Router, Route, Switch } from 'react-router-dom'
import Panel from '../../components/Panel/Panel'
import FlightsSummary from '../../components/FlightsSummary/FlightsSummary'
import logo from '../../static/img/logos/transavia-logo-vertical.svg'
import SummaryTable from './SummaryTable'
import objectContainsFalse from '../../helpers/objectContainsFalse'
import './Sidebar.scss'
import { resetFlow } from '../../actions/reset'
import { removeFlight, updateFlightOffers } from '../../actions/flightOffers'
import routes from '../../config/routes'
import history from '../../config/history'

class Sidebar extends React.Component {
  state = {
    isRestartSearchEnabled: false,
    isSelectFlightsEnabled: false,
    isBookerDetailsEnabled: false,
    isGroupDetailsEnabled: false,
  }

  static getDerivedStateFromProps = ({ validation, searchDetails }, state) => {
    return {
      ...state,
      isRestartSearchEnabled: history.location.pathname !== routes.rootPath,
      isSelectFlightsEnabled: !objectContainsFalse({
        minAdult: validation.groupDetails.party.minAdult,
        minGroupSize: validation.groupDetails.party.minGroupSize,
        routeCombination: validation.searchDetails.routeCombination,
        ...validation.searchDetails.primary,
        ...(searchDetails.isMultipleDestination
          ? validation.searchDetails.secondary
          : null),
      }),
      isBookerDetailsEnabled: !objectContainsFalse({
        ...validation.ancillaries,
        ...(Object.keys(validation.flightOffers).length
          ? validation.flightOffers
          : { flightOffers: false }),
      }),
      isGroupDetailsEnabled: !objectContainsFalse(validation.bookerDetails),
    }
  }

  getTotalSelectedAncillaries() {
    const { ancillaries } = this.props
    return Object.keys(ancillaries).reduce(
      (x, y) => x + ancillaries[y].value,
      0
    )
  }

  getAncillariesWithLabels() {
    const { ancillaries, content } = this.props
    const ancillariesContent = content('Ancillaries')
    return Object.keys(ancillaries).reduce((a, x) => {
      const ancillary = ancillaries[x]
      return [
        ...a,
        { ...ancillary, label: ancillariesContent[ancillary.labelId] },
      ]
    }, [])
  }

  removeSelectedFlight = (routeId, flightId) => {
    const { removeFlight, flightOffers, updateFlightOffers } = this.props
    const isSelectingFlights = history.location.pathname.includes(
      routes.flightOffers
    )
    const routeToBeEdited = flightOffers.selectedFlights.find(
      x => x.routeId === routeId
    )
    if (routeToBeEdited.flights.length > 1) {
      removeFlight(routeId, flightId)
    } else {
      updateFlightOffers({
        ...flightOffers,
        selectedFlights: flightOffers.selectedFlights.filter(
          x => x.routeId !== routeId
        ),
      })
      if (!isSelectingFlights) history.push(routes.flightOffers)
    }
  }

  render() {
    const {
      flightOffers,
      bookerDetails,
      searchDetails,
      content,
      resetFlow,
    } = this.props
    const {
      isBookerDetailsEnabled,
      isRestartSearchEnabled,
      isGroupDetailsEnabled,
      isSelectFlightsEnabled,
    } = this.state
    const labels = content('Summary Panel')

    return (
      <Router history={history}>
        <aside className="Sidebar">
          <Switch>
            <Route
              path="/(/|summary|success)/"
              exact
              render={() => (
                <div className="sidebarLogo">
                  <img src={logo} alt="Transavia" height="200px" width="auto" />
                </div>
              )}
            />

            <Route
              render={() => [
                <Panel
                  key="restart"
                  title={labels['Restart Search']}
                  icon="reset"
                  onClick={isRestartSearchEnabled ? () => resetFlow() : null}
                  isLinkEnabled={isRestartSearchEnabled}
                  linkTo="/"
                />,
                <Panel
                  key="flights"
                  title={labels['Select Flights']}
                  icon="outbound"
                  isLinkEnabled={isSelectFlightsEnabled}
                  linkTo="/flight-offers"
                  hasChildren={flightOffers.selectedFlights.length > 0}
                >
                  <FlightsSummary
                    selectedFlights={flightOffers.selectedFlights}
                    removeFlight={this.removeSelectedFlight}
                    selectedAirports={{
                      outboundAirport: searchDetails.primary.outboundAirport,
                      inboundAirport: searchDetails.primary.inboundAirport,
                      secondaryOutboundAirport:
                        searchDetails.secondary.outboundAirport,
                      secondaryInboundAirport:
                        searchDetails.secondary.inboundAirport,
                    }}
                    isMultipleDestination={searchDetails.isMultipleDestination}
                  />
                </Panel>,
                <Panel
                  key="ancillaries"
                  title={labels['Add Ancillaries']}
                  icon="medium-luggage"
                  linkTo="/ancillaries"
                  isLinkEnabled={isBookerDetailsEnabled}
                  hasChildren={this.getTotalSelectedAncillaries() > 0}
                >
                  <SummaryTable summary={this.getAncillariesWithLabels()} />
                </Panel>,
                <Panel
                  key="bookerDetails"
                  title={labels['Add Booker Details']}
                  icon="mainbooker"
                  isLinkEnabled={isBookerDetailsEnabled}
                  linkTo="/booker-details"
                  hasChildren={
                    bookerDetails.firstName.length > 0 &&
                    bookerDetails.lastName.length > 0
                  }
                >
                  <p className="booker-name">
                    <strong>
                      {`${bookerDetails.firstName} ${bookerDetails.lastName}`}
                    </strong>
                  </p>
                </Panel>,
                <Panel
                  key="groupDetails"
                  title={labels['Add Group Details']}
                  icon="groups"
                  isLinkEnabled={isGroupDetailsEnabled}
                  linkTo="/group-details"
                />,
              ]}
            />
          </Switch>
        </aside>
      </Router>
    )
  }
}

Sidebar.propTypes = {
  flightOffers: PropTypes.object,
  content: PropTypes.func,
  ancillaries: PropTypes.object,
  bookerDetails: PropTypes.object,
  searchDetails: PropTypes.object,
  validation: PropTypes.object,
  resetFlow: PropTypes.func,
  removeFlight: PropTypes.func,
  updateFlightOffers: PropTypes.func,
}

const mapStateToProps = state => {
  const {
    validation,
    searchDetails,
    flightOffers,
    bookerDetails,
    ancillaries,
  } = state

  return {
    validation,
    searchDetails,
    flightOffers,
    bookerDetails,
    ancillaries,
  }
}

const mapDispatchToProps = {
  resetFlow,
  removeFlight,
  updateFlightOffers,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Sidebar)
